import PropTypes from 'prop-types';
import DateInput from 'components/forms/dynamic-form-inputs/date-input';
import TitleField from '../fields/title-field';
import PrimaryProposalUnitField from '../fields/primary-proposal-unit-field';
import ContactField from '../fields/contacts-field';
import SolicitorsField from '../fields/solicitors/solicitors-field';
import DescriptionField from '../fields/description-field';
import ProposalTypeField from '../fields/proposal-type-field';
import ProposalFormController from '../proposal-form-controller';
import { useEffect } from 'react';
import { ContactCard } from '@evertrue/et-components';
import { DynamicField } from '@evertrue/sdk';
import { useProposalFormContext } from '../context/proposal-form-context';
import { ModalBody, ModalHeaderWithActions } from '@evertrue/et-components';
import StageField from '../fields/stage-field';
import SwitchInput from 'components/forms/dynamic-form-inputs/switch-input';
import AmountField from '../fields/amount-field';
import CustomFields from 'components/forms/dynamic-form-inputs/custom-fields/custom-fields';
import DesignationsField from '../fields/designations-field';
import AdditionalProposalCategoryField from '../fields/additional-proposal-category-field';

const ProposalFormBody = ({
  // Formik Props
  handleSubmit,
  handleChange,
  setFieldValue,
  setFieldTouched,
  values,
  errors,
  dirty,
  isValid,
  touched,
  submitCount = 0,
  // There are tons of additional props that are passed down from the Formik form, but we don't destructure them all here.
  editing = false,
  proposal = {},
  primaryContactId,
  setProposal = () => null,
  closeModal = () => null,
  afterCreate = () => null,
}) => {
  const {
    additionalContactLimit,
    solicitorLimit,
    hasProposalReporting,
    designationLimit,
    hasCustomFields,
    customFields,
  } = useProposalFormContext();

  // Populate the proposal with the primary contact and additional contacts
  // This is done after the initial render because we need to fetch the contact records from the API and Decorate them.
  useEffect(() => {
    const fetchContacts = async () => {
      const ids = Array.isArray(proposal.contacts)
        ? proposal.contacts.map((contact) => contact?.contact_id).filter((id) => !!id)
        : [primaryContactId];
      try {
        const contacts = await ProposalFormController.fetchParsedContacts(ids);
        const [primaryContact] = contacts.splice(
          contacts.findIndex((contact) => contact.id === primaryContactId),
          1
        );
        setFieldValue('contacts', contacts);
        setFieldTouched('contacts', true);
        setFieldValue('primary_contact', primaryContact);
        setFieldTouched('primary_contact', true);
      } catch {}
    };

    fetchContacts();
  }, [proposal.contacts, primaryContactId, setFieldValue, setFieldTouched]);

  useEffect(() => {
    // If we are not editing an existing proposal, then we need to populate the Custom Field object with empty values.
    // This ensures the dynamic form validates each field individually.
    if (!editing) {
      const touchedCustomFields = {};
      const customFieldObject =
        hasCustomFields && customFields
          ? customFields.reduce((acc, field) => {
              touchedCustomFields[field.id] = false; // Initialize touched state for custom fields
              acc[field.id] = ''; // Default value for custom fields is an empty string
              return acc;
            }, {})
          : {};
      setFieldValue('custom_field', customFieldObject);
      setFieldTouched('custom_field', touchedCustomFields);
    }
  }, [setFieldValue, setFieldTouched, customFields, hasCustomFields, editing]);

  return (
    <>
      <ModalHeaderWithActions
        title={values.proposal_id ? 'Edit Proposal' : 'Add Proposal'}
        closeModal={closeModal}
        onSubmit={handleSubmit}
        disableSubmit={!isValid}
      />
      <ModalBody>
        <ContactCard contact={values.primary_contact || {}} showNameLink={false} showClassYear={true} />
        {additionalContactLimit > 0 && (
          <DynamicField
            name="contacts"
            label={`Additional Prospects (${additionalContactLimit} maximum)`}
            values={values}
            as={ContactField}
            contactId={primaryContactId}
          />
        )}
        <DynamicField name="title" values={values} as={TitleField} />
        <DynamicField name="description" values={values} as={DescriptionField} />
        <div className="form-row">
          <DynamicField name="created_date" values={values} as={DateInput} triggerClassName="ignore-margin" />
          <DynamicField name="proposal_type" values={values} as={ProposalTypeField} />
        </div>
        <DynamicField name="primary_proposal_unit" values={values} as={PrimaryProposalUnitField} />
        <DynamicField
          name="solicitors"
          label={solicitorLimit ? `Solicitors (max ${solicitorLimit})` : 'Solicitors'}
          boldLabel
          values={values}
          as={SolicitorsField}
        />
        <div className="form-row">
          <DynamicField name="status" label="Stage" values={values} as={StageField} />
          <DynamicField name="active" values={values} as={SwitchInput} />
        </div>
        {ProposalFormController.amountFields.map((field) => (
          <AmountField key={field} name={field} label={field === 'ask' ? 'Current Ask' : field} values={values} />
        ))}
        <DynamicField
          name="reported"
          values={{ ...values, proposal_reported_gate_enabled: hasProposalReporting }}
          as={SwitchInput}
          label="Launchpad"
        />
        <DynamicField
          name="proposal_designations"
          label={designationLimit ? `Designations (${designationLimit} maximum)` : 'Designations'}
          values={values}
          as={DesignationsField}
        />
        <DynamicField name="additional_proposal_category" values={values} as={AdditionalProposalCategoryField} />
        {/* NOTE: Custom Fields are handled differently -- they use the "component" prop, not the "as" prop. See the difference on Formik's docs */}
        <DynamicField
          name="custom_field"
          values={values}
          component={CustomFields}
          wrap={false}
          orgCustomFields={customFields}
          hasCustomFields={hasCustomFields}
        />
      </ModalBody>
    </>
  );
};

ProposalFormBody.propTypes = {
  // Formik Props
  handleSubmit: PropTypes.func,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func,
  setFieldTouched: PropTypes.func,
  values: PropTypes.object,
  errors: PropTypes.object,
  dirty: PropTypes.bool,
  isValid: PropTypes.bool,
  touched: PropTypes.object,
  submitCount: PropTypes.number,
  // Custom Props
  editing: PropTypes.bool,
  proposal: PropTypes.object,
  primaryContactId: PropTypes.number,
  setProposal: PropTypes.func,
  closeModal: PropTypes.func,
  afterCreate: PropTypes.func,
};

export default ProposalFormBody;
